import React, { useEffect, useState } from "react";
import {
    Row, Col, Card, CardHeader, CardBody, Button, Alert, Spinner,
    Modal, ModalHeader, ModalBody, ModalFooter, Input,
} from "reactstrap";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { API } from "../../api/urls";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
    COMPANY_TYPE_LABEL_MAP,
    COMPANY_TYPE_BADGE_COLORS,
} from "../../constants/companyTypes";
import BackButton from "../../components/Common/BackButton";

const NAVY = "#0A316E";

const WORKING_AREA_LABELS = {
    INFORMATION_TECHNOLOGY: "Bilişim ve Yazılım",
    MANUFACTURING: "Üretim ve İmalat",
    RETAIL: "Perakende Satış",
    HEALTHCARE: "Sağlık ve Tıp",
    FINANCE: "Finans ve Sigorta",
    EDUCATION: "Eğitim Hizmetleri",
    AUTOMOTIVE: "Otomotiv Sektörü",
    FOOD_AND_BEVERAGE: "Gıda ve İçecek",
    CONSTRUCTION: "İnşaat Sektörü",
    LOGISTICS: "Lojistik ve Nakliye",
    ENERGY: "Enerji",
    OTHER: "Diğer",
};

const STATUS_CONFIG = {
    PENDING:  { label: "Beklemede",  color: "#E56A22" },
    APPROVED: { label: "Onaylandı",  color: "#71C29B" },
    REJECTED: { label: "Reddedildi", color: "#E07A7A" },
};

const StatusBadge = ({ status }) => {
    const cfg = STATUS_CONFIG[status] || { label: status, color: "#B0B7C3" };
    return (
        <span
            className="badge text-white px-3 py-2"
            style={{ backgroundColor: cfg.color, borderRadius: 999, fontSize: "0.85rem" }}
        >
            {cfg.label}
        </span>
    );
};

const fmtDateTime = (v) => {
    if (!v) return "-";
    try { return new Date(v).toLocaleString("tr-TR"); } catch { return v; }
};

/* ────────────── Review Modal ────────────── */
const ReviewModal = ({ isOpen, toggle, request, action, onConfirm }) => {
    const [reason, setReason] = useState("");
    const [loading, setLoading] = useState(false);

    useEffect(() => { if (isOpen) setReason(""); }, [isOpen]);

    if (!request) return null;
    const isApprove = action === "APPROVED";

    const handleSubmit = async () => {
        setLoading(true);
        try {
            const payload = { approved: isApprove };
            if (!isApprove && reason.trim()) payload.rejectionReason = reason.trim();

            const res = await axios.post(API.admin_registration_review(request.id), payload);
            if (res.data.statusCode === 200) {
                onConfirm({
                    success: true,
                    message: res.data.message || (isApprove ? "Başvuru onaylandı. Firma ve kullanıcı oluşturuldu." : "Başvuru reddedildi."),
                });
            } else {
                onConfirm({ success: false, message: res.data.message || "İşlem başarısız oldu." });
            }
        } catch (err) {
            onConfirm({ success: false, message: err.response?.data?.message || "Bir hata oluştu." });
        } finally {
            setLoading(false);
            toggle();
        }
    };

    return (
        <Modal isOpen={isOpen} toggle={toggle}>
            <ModalHeader toggle={toggle}>
                {isApprove ? "Başvuruyu Onayla" : "Başvuruyu Reddet"}
            </ModalHeader>
            <ModalBody>
                <p>
                    <strong>{request.companyName}</strong> ({request.email}) başvurusunu{" "}
                    {isApprove ? "onaylamak" : "reddetmek"} istediğinizden emin misiniz?
                </p>
                {isApprove && (
                    <Alert color="info" className="mb-0">
                        <i className="bx bx-info-circle me-1" />
                        Onay verildiğinde firma ve kullanıcı hesabı otomatik oluşturulacak ve
                        hoşgeldiniz e-postası gönderilecektir.
                    </Alert>
                )}
                {!isApprove && (
                    <div className="mt-3">
                        <label className="form-label">Red Nedeni</label>
                        <Input
                            type="textarea"
                            rows={3}
                            placeholder="Red nedenini yazınız (opsiyonel)"
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                        />
                    </div>
                )}
            </ModalBody>
            <ModalFooter>
                <Button color="secondary" onClick={toggle} disabled={loading}>İptal</Button>
                <Button color={isApprove ? "success" : "danger"} onClick={handleSubmit} disabled={loading}>
                    {loading ? <Spinner size="sm" /> : isApprove ? "Onayla" : "Reddet"}
                </Button>
            </ModalFooter>
        </Modal>
    );
};

/* ────────────── Main Page ────────────── */
const RegistrationRequestDetailPage = () => {
    const navigate = useNavigate();
    const { id } = useParams();

    const [loading, setLoading] = useState(true);
    const [fetchError, setFetchError] = useState("");
    const [req, setReq] = useState(null);

    const [reviewAction, setReviewAction] = useState("");
    const [reviewModalOpen, setReviewModalOpen] = useState(false);

    const fetchRequest = async () => {
        setLoading(true);
        setFetchError("");
        try {
            const { data } = await axios.get(API.admin_registration(id));
            if (data?.statusCode === 200) {
                setReq(data.data);
            } else {
                setFetchError("Başvuru bilgileri getirilemedi");
            }
        } catch (err) {
            if (err.response?.status === 401 || err.response?.status === 403) {
                navigate("/login");
                return;
            }
            if (err.response?.status === 404) {
                setFetchError("Başvuru bulunamadı");
                return;
            }
            setFetchError(err.response?.data?.message || "Başvuru bilgileri alınırken hata oluştu");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        document.title = "Kayıt Talebi Detayı | Tahsilat360";
        fetchRequest();
    }, [id]);

    const openReview = (action) => {
        setReviewAction(action);
        setReviewModalOpen(true);
    };

    const styles = `
    .reg-detail .btn-navy { background:${NAVY}; border-color:${NAVY}; color:#fff; }
    .reg-detail .btn-navy:hover { filter:brightness(1.03); }
    .reg-detail .rounded-4 { border-radius:1rem!important; }
    .reg-detail .pill { border-radius:999px!important; }

    .reg-detail table {
      width:100%;
      border-collapse:separate;
      border-spacing:0;
      border:1px solid #dee2e6;
      border-radius:1rem;
      overflow:hidden;
      background:#fff;
    }
    .reg-detail table th {
      background:#f9fafb;
      color:#495057;
      font-weight:600;
      border-bottom:1px solid #dee2e6;
      border-right:1px solid #dee2e6;
    }
    .reg-detail table td {
      border-bottom:1px solid #e9ecef;
      border-right:1px solid #e9ecef;
      color:#212529;
      background-color:#fff;
    }
    .reg-detail table tr:last-child td { border-bottom:none; }
    .reg-detail table tr td:last-child,
    .reg-detail table tr th:last-child { border-right:none; }
    .reg-detail table td, .reg-detail table th {
      padding:.85rem 1rem; vertical-align:middle;
    }
    .reg-detail table tr:hover td {
      background:#f8f9fa; transition:background .2s ease-in-out;
    }
    @media (max-width:768px) {
      .reg-detail table td, .reg-detail table th { padding:.6rem .7rem; }
    }
  `;

    return (
        <div className="page-content reg-detail">
            <style>{styles}</style>
            <div className="container-fluid">
                {/* Breadcrumb & Actions */}
                <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                    <div className="d-flex align-items-center gap-3">
                        <BackButton />
                        <div className="d-flex align-items-center text-muted small">
                            <span
                                className="text-secondary fw-bold"
                                role="button"
                                onClick={() => navigate("/registration-requests")}
                                style={{ cursor: "pointer" }}
                            >
                                Kayıt Talepleri
                            </span>
                            <span className="mx-2">/</span>
                            <span className="fw-semibold">Başvuru Detayı</span>
                        </div>
                    </div>
                    <div className="d-flex gap-2 flex-wrap">
                        {req?.status === "PENDING" && (
                            <>
                                <Button
                                    color="success"
                                    size="sm"
                                    className="rounded-pill px-3"
                                    onClick={() => openReview("APPROVED")}
                                >
                                    <i className="bx bx-check me-1" /> Onayla
                                </Button>
                                <Button
                                    color="danger"
                                    size="sm"
                                    className="rounded-pill px-3"
                                    onClick={() => openReview("REJECTED")}
                                >
                                    <i className="bx bx-x me-1" /> Reddet
                                </Button>
                            </>
                        )}
                    </div>
                </div>

                <Row>
                    <Col lg={12}>
                        <Card className="bg-white border-0 shadow-lg rounded-4">
                            <CardHeader className="bg-white border-0 pb-0 rounded-top-4">
                                <h5 className="mb-0 text-dark fw-semibold">
                                    <i className="bx bx-user-check me-2" />
                                    {req?.companyName || "Başvuru Detayı"}
                                </h5>
                                {req?.id && (
                                    <small className="text-muted d-block mt-1">
                                        Başvuru ID: <code className="text-dark">{req.id}</code>
                                    </small>
                                )}
                            </CardHeader>

                            <CardBody className="p-4">
                                {fetchError && (
                                    <Alert color="danger" className="border-0 rounded-4">
                                        <i className="bx bx-error me-2" />
                                        {fetchError}
                                        <Button size="sm" color="danger" className="ms-2 pill" onClick={fetchRequest}>
                                            Tekrar Dene
                                        </Button>
                                    </Alert>
                                )}

                                {loading ? (
                                    <div className="text-center p-5">
                                        <Spinner color="primary" />
                                        <div className="mt-2 text-muted">Başvuru bilgileri yükleniyor...</div>
                                    </div>
                                ) : req && (
                                    <>
                                        {/* Firma & Iletisim */}
                                        <Row className="g-4">
                                            <Col md={6}>
                                                <h5 className="text-muted mb-3">Firma Bilgileri</h5>
                                                <div className="table-responsive">
                                                    <table className="table table-bordered table-sm">
                                                        <tbody>
                                                        <tr><th width="40%">Firma Adı</th><td>{req.companyName}</td></tr>
                                                        <tr>
                                                            <th>Firma Türü</th>
                                                            <td>
                                                                {req.companyType ? (
                                                                    <span
                                                                        className="badge text-white px-3 py-2"
                                                                        style={{
                                                                            backgroundColor: COMPANY_TYPE_BADGE_COLORS[req.companyType] || "#6c757d",
                                                                            borderRadius: 999,
                                                                            fontSize: "0.85rem",
                                                                        }}
                                                                    >
                                                                        {COMPANY_TYPE_LABEL_MAP[req.companyType] || req.companyType}
                                                                    </span>
                                                                ) : "-"}
                                                            </td>
                                                        </tr>
                                                        <tr><th>Faaliyet Alanı</th><td>{WORKING_AREA_LABELS[req.workingArea] || req.workingArea || "-"}</td></tr>
                                                        <tr><th>Alt Domain</th><td>{req.subdomain ? <code>{req.subdomain}</code> : "-"}</td></tr>
                                                        <tr><th>Domain</th><td>{req.domain ? <code>{req.domain}</code> : "-"}</td></tr>
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </Col>

                                            <Col md={6}>
                                                <h5 className="text-muted mb-3">İletişim Bilgileri</h5>
                                                <div className="table-responsive">
                                                    <table className="table table-bordered table-sm">
                                                        <tbody>
                                                        <tr><th width="40%">Yetkili Kişi</th><td>{req.contactFullName || "-"}</td></tr>
                                                        <tr>
                                                            <th>E-posta</th>
                                                            <td>{req.email ? <a href={`mailto:${req.email}`}>{req.email}</a> : "-"}</td>
                                                        </tr>
                                                        <tr><th>Telefon</th><td>{req.phoneNumber || "-"}</td></tr>
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </Col>
                                        </Row>

                                        {/* Vergi & Durum */}
                                        <Row className="g-4 mt-1">
                                            <Col md={6}>
                                                <h5 className="text-muted mb-3">Vergi / Kimlik Bilgileri</h5>
                                                <div className="table-responsive">
                                                    <table className="table table-bordered table-sm">
                                                        <tbody>
                                                        <tr><th width="40%">Vergi Kimlik No</th><td>{req.invoiceTaxNumber || "-"}</td></tr>
                                                        <tr><th>TC Kimlik No</th><td>{req.invoiceTcNumber || "-"}</td></tr>
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </Col>

                                            <Col md={6}>
                                                <h5 className="text-muted mb-3">Başvuru Durumu</h5>
                                                <div className="table-responsive">
                                                    <table className="table table-bordered table-sm">
                                                        <tbody>
                                                        <tr><th width="40%">Durum</th><td><StatusBadge status={req.status} /></td></tr>
                                                        <tr><th>Başvuru Tarihi</th><td>{fmtDateTime(req.createdAt)}</td></tr>
                                                        {req.reviewedAt && (
                                                            <tr><th>Değerlendirme Tarihi</th><td>{fmtDateTime(req.reviewedAt)}</td></tr>
                                                        )}
                                                        {req.status === "REJECTED" && req.rejectionReason && (
                                                            <tr>
                                                                <th>Red Nedeni</th>
                                                                <td><span className="text-danger">{req.rejectionReason}</span></td>
                                                            </tr>
                                                        )}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </Col>
                                        </Row>

                                        {req.referralCode && (
                                            <Row className="g-4 mt-1">
                                                <Col md={12}>
                                                    <h5 className="text-muted mb-3">Davet (Arkadaşını Getir)</h5>
                                                    <div className="table-responsive">
                                                        <table className="table table-bordered table-sm">
                                                            <tbody>
                                                            <tr><th width="20%">Davet Kodu</th><td><code>{req.referralCode}</code></td></tr>
                                                            <tr><th>Davet Eden Firma</th><td>{req.referrerCompanyName || "-"}</td></tr>
                                                            {req.referralProblem && (
                                                                <tr>
                                                                    <th>Uyarı</th>
                                                                    <td>
                                                                        <span className="text-danger">{req.referralProblem}</span>
                                                                        <div className="text-muted" style={{ fontSize: ".85rem" }}>
                                                                            Onaylarsanız firma oluşturulur ama davet ödülü verilmez.
                                                                        </div>
                                                                    </td>
                                                                </tr>
                                                            )}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                </Col>
                                            </Row>
                                        )}

                                        {/* Teknik Bilgiler */}
                                        {(req.ipAddress || req.userAgent || req.acceptLanguage) && (
                                            <Row className="g-4 mt-1">
                                                <Col md={12}>
                                                    <h5 className="text-muted mb-3">Teknik Bilgiler</h5>
                                                    <div className="table-responsive">
                                                        <table className="table table-bordered table-sm">
                                                            <tbody>
                                                            {req.ipAddress && (
                                                                <tr><th width="20%">IP Adresi</th><td><code>{req.ipAddress}</code></td></tr>
                                                            )}
                                                            {req.userAgent && (
                                                                <tr>
                                                                    <th>Tarayıcı</th>
                                                                    <td style={{ fontSize: ".85rem", wordBreak: "break-all" }}>{req.userAgent}</td>
                                                                </tr>
                                                            )}
                                                            {req.acceptLanguage && (
                                                                <tr><th>Dil Tercihi</th><td>{req.acceptLanguage}</td></tr>
                                                            )}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                </Col>
                                            </Row>
                                        )}
                                    </>
                                )}
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </div>

            <ReviewModal
                isOpen={reviewModalOpen}
                toggle={() => setReviewModalOpen(false)}
                request={req}
                action={reviewAction}
                onConfirm={({ success, message }) => {
                    setReviewModalOpen(false);
                    if (success) {
                        toast.success(message);
                        fetchRequest();
                    } else {
                        toast.error(message);
                    }
                }}
            />

            <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} newestOnTop={false} closeOnClick pauseOnFocusLoss draggable pauseOnHover />
        </div>
    );
};

export default RegistrationRequestDetailPage;
